import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Menu, MenuItem, TextField, InputAdornment, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import { useOrders } from '../../hooks/useOrders';
import { OrderStatus } from '../../types/orders.types';
import { NewPagination } from '../../components/Common/Pagination/Pagination';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'disputed' | 'refunds'>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<string | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState<null | HTMLElement>(null);
  const [clientMenuAnchor, setClientMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  // Map section to status for backend query
  // - 'all' section: use undefined (or selected status filter) to get all orders
  // - 'disputed' section: get cancelled orders
  // - 'refunds' section: get refunded orders
  const effectiveStatus = useMemo(() => {
    if (activeTab === 'disputed') {
      return OrderStatus.CANCELLED;
    } else if (activeTab === 'refunds') {
      return OrderStatus.REFUNDED;
    } else if (activeTab === 'all') {
      // For 'all' section, use the selected status filter or undefined to get all orders
      return selectedStatus || undefined;
    }
    return selectedStatus;
  }, [activeTab, selectedStatus]);

  // Use the orders hook with current filters
  // Pass effectiveStatus to enable new cursor-based pagination
  // KEY INSIGHT: We add activeTab as a dependency to force hook remount on tab switch
  // This completely resets all state including refs, preventing cursor carryover
  const {
    orders,
    allOrders,
    loading,
    currentPage,
    setCurrentPage,
    hasMore,
    totalPages,
    totalItems,
    pageStartIndex,
    pageEndIndex,
    updateOrderStatus,
    vendors,
    users,
    vendorsLoading,
    usersLoading,
  } = useOrders({
    section: activeTab,
    status: effectiveStatus, // This enables cursor pagination
    vendorId: selectedVendorId,
    userId: selectedUserId,
    search: searchQuery,
    limit: 10,
  });

  // Handle search with debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Reset to page 1 when status changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, setCurrentPage]);

  // Reset status filter when switching tabs
  useEffect(() => {
    if (activeTab === 'disputed' || activeTab === 'refunds') {
      // For disputed and refunds tabs, clear the status filter
      // The effectiveStatus will handle setting the correct status
      setSelectedStatus(undefined);
    }
    // Reset to page 1 when tab changes
    setCurrentPage(1);
  }, [activeTab, setCurrentPage]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedOrder(orderId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleStatusChange = async (newStatus: OrderStatus) => {
    if (selectedOrder) {
      await updateOrderStatus(selectedOrder, newStatus);
    }
    handleMenuClose();
  };

  const handleCancelOrder = async () => {
    if (selectedOrder) {
      await updateOrderStatus(selectedOrder, OrderStatus.CANCELLED);
    }
    handleMenuClose();
  };

  const handleRefund = async () => {
    if (selectedOrder) {
      await updateOrderStatus(selectedOrder, OrderStatus.REFUNDED);
    }
    handleMenuClose();
  };

  // Format date from timestamp
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `QAR ${amount.toFixed(2)}`;
  };

  // Count disputed and refund orders
  const disputedCount = useMemo(() => allOrders.filter(o => o.hasDispute).length, [allOrders]);
  const refundCount = useMemo(() => allOrders.filter(o => o.hasRefund).length, [allOrders]);

  // Get selected vendor name
  const selectedVendorName = useMemo(() => {
    if (!selectedVendorId) return 'All vendors';
    const vendor = vendors.find(v => v.vendorId === selectedVendorId);
    return vendor?.name || 'All vendors';
  }, [selectedVendorId, vendors]);

  // Get selected user name
  const selectedUserName = useMemo(() => {
    if (!selectedUserId) return 'All clients';
    const user = users.find(u => u.userId === selectedUserId);
    return user?.name || 'All clients';
  }, [selectedUserId, users]);

  // Get selected status label
  const selectedStatusLabel = useMemo(() => {
    return selectedStatus || 'All statuses';
  }, [selectedStatus]);

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.APPROVED:
        return 'var(--palette-light-status-success-value-hex)';
      case OrderStatus.PENDING:
        return 'var(--palette-light-text-tertiary-value-hex)';
      case OrderStatus.CANCELLED:
      case OrderStatus.DECLINED:
        return 'var(--palette-light-status-danger-value-hex)';
      case OrderStatus.REFUNDED:
        return 'var(--palette-light-status-warning-value-hex)';
      case OrderStatus.COMPLETED:
        return 'var(--palette-light-background-tertiary-value-hex)';
      case OrderStatus.CHANGING:
        return '#FFA500'; // Orange color for changing status
      case OrderStatus.IN_PROGRESS:
      case OrderStatus.DELIVERY:
        return 'var(--palette-light-brand-secondary-value-hex)';
      case OrderStatus.DRAFT:
      default:
        return 'var(--palette-light-text-primary-value-hex)';
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 'var(--palette-light-6-value)', // 24px
      padding: {
        xs: '24px 16px',
        sm: '40px 40px',
        md: '40px 80px',
      },
      width: '100%',
      minHeight: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Page Title and Search */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--palette-light-4-value)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)', flex: 1 }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h1-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
            Orders
          </Typography>
        </Box>
        <TextField
          placeholder="Search orders"
          variant="outlined"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '280px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: 'var(--palette-light-3-value)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              padding: 'var(--palette-light-3-value)',
            },
          }}
        />
      </Box>

      {/* Tabs and Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--palette-light-2-value)' }}>
        {/* Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-2-value)' }}>
          <Button
            onClick={() => setActiveTab('all')}
            sx={{
              backgroundColor: activeTab === 'all' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
              color: activeTab === 'all' ? 'var(--palette-light-text-foreground-value-hex)' : 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: activeTab === 'all' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            All
          </Button>
          <Button
            onClick={() => setActiveTab('disputed')}
            sx={{
              backgroundColor: activeTab === 'disputed' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
              color: activeTab === 'disputed' ? 'var(--palette-light-text-foreground-value-hex)' : 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--palette-light-2-value)',
              '&:hover': {
                backgroundColor: activeTab === 'disputed' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            Disputed
            {disputedCount > 0 && (
              <Box sx={{
                backgroundColor: 'var(--palette-light-status-success-value-hex)',
                color: 'var(--palette-light-text-foreground-value-hex)',
                fontSize: 'var(--palette-light-font-size-small-value)',
                fontWeight: 700,
                borderRadius: '20px',
                padding: '2px 2px 0',
                minWidth: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '-2px',
              }}>
                {disputedCount}
              </Box>
            )}
          </Button>
          <Button
            onClick={() => setActiveTab('refunds')}
            sx={{
              backgroundColor: activeTab === 'refunds' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
              color: activeTab === 'refunds' ? 'var(--palette-light-text-foreground-value-hex)' : 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--palette-light-2-value)',
              '&:hover': {
                backgroundColor: activeTab === 'refunds' ? '#AC654D' : 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            Refunds
            {refundCount > 0 && (
              <Box sx={{
                backgroundColor: 'var(--palette-light-status-success-value-hex)',
                color: 'var(--palette-light-text-foreground-value-hex)',
                fontSize: 'var(--palette-light-font-size-small-value)',
                fontWeight: 700,
                borderRadius: '20px',
                padding: '2px 2px 0',
                minWidth: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '-2px',
              }}>
                {refundCount}
              </Box>
            )}
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', flexWrap: 'wrap' }}>
          <Button
            onClick={(e) => setVendorMenuAnchor(e.currentTarget)}
            sx={{
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              color: 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            {selectedVendorName}
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
          </Button>
          <Button
            onClick={(e) => setClientMenuAnchor(e.currentTarget)}
            sx={{
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              color: 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            {selectedUserName}
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
          </Button>
          <Button
            onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
            sx={{
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              color: 'var(--palette-light-text-primary-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              height: '39px',
              padding: '10px 20px 7px',
              borderRadius: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.9,
              },
            }}
          >
            {selectedStatusLabel}
            <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
        </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
        {/* Table Header */}
        <Box sx={{ 
          display: 'flex', 
          alignSelf: 'stretch', 
          gap: 'var(--palette-light-4-value)', // 16px
          padding: 'var(--palette-light-3-value) 0', // 12px vertical
          borderBottom: '1px solid #F1F4F4'
        }}>
          <Box sx={{ width: '90px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              ID
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Vendor
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Customer / Event
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Product
            </Typography>
          </Box>
          <Box sx={{ width: '130px' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Total
            </Typography>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Delivery
            </Typography>
          </Box>
          <Box sx={{ width: '100px' }} />
          <Box sx={{ width: '56px' }} />
        </Box>

        {/* Table Rows */}
        {loading && orders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--palette-light-10-value)' }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--palette-light-10-value)' }}>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              No orders found
            </Typography>
          </Box>
        ) : (
          orders.map((order) => (
            <Box 
              key={order.orderId}
              onClick={() => navigate(`/orders/${order.orderId}`)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                alignSelf: 'stretch', 
                gap: 'var(--palette-light-4-value)', // 16px
                padding: 'var(--palette-light-4-value) 0', // 16px vertical
                borderBottom: '1px solid #F1F4F4',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                },
              }}
            >
              {/* ID Column */}
              <Box sx={{ width: '90px', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', width: '60px' }}>
                  {order.orderNumber}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {formatDate(order.createdAt)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-3-value)' }}>
                  {order.hasDispute && (
                    <Box
                      component="img"
                      src="/icons/dispute-icon.svg"
                      alt="Dispute"
                      sx={{ width: '24px', height: '24px' }}
                    />
                  )}
                  {order.hasRefund && (
                    <Box
                      component="img"
                      src="/icons/refund-icon.svg"
                      alt="Refund"
                      sx={{ width: '24px', height: '24px' }}
                    />
                  )}
                </Box>
              </Box>

              {/* Vendor Column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {order.vendor.name}
                  </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  {order.vendor.category || 'N/A'}
                  </Typography>
                </Box>

              {/* Customer / Event Column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {order.customer.name}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {order.customer.phone}
                </Typography>
                {order.event?.dateTime && (
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {order.event.dateTime}
                  </Typography>
                )}
                {order.event?.address && (
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {order.event.address}
                  </Typography>
                )}
              </Box>

              {/* Product Column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {order.product.name}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {order.product.details}
                  </Typography>
              </Box>

              {/* Total Column */}
              <Box sx={{ width: '130px', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {formatCurrency(((order.total || 0) - (order.discountAmount || 0)) + (order.shippingFee || 0))}
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  {formatCurrency(order.shippingFee || 0)}
                </Typography>
              </Box>

              {/* Status Column */}
              <Box sx={{ width: '100px', display: 'flex', justifyContent: 'flex-start' }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0 var(--palette-light-3-value)', // 12px horizontal
                  height: 'var(--palette-light-8-value)', // 32px
                  backgroundColor: getStatusColor(order.status),
                  borderRadius: 'var(--palette-light-2-value)', // 8px
                }}>
                  <Typography sx={{ 
                    fontSize: 'var(--palette-light-font-size-small-value)', 
                    color: 'var(--palette-light-text-foreground-value-hex)',
                  }}>
                    {order.status}
                  </Typography>
                </Box>
        </Box>

              {/* Actions Column */}
              <Box sx={{ width: '56px', display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, order.orderId)}
                  sx={{
                    width: '56px',
                    height: '56px',
                    borderRadius: 'var(--palette-light-4-value)', // 16px
                    color: 'var(--palette-light-brand-primary-value-hex)',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 49, 79, 0.04)',
                    }
                  }}
                >
                  <MoreHorizIcon />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
          </Box>

      {/* Pagination */}
      {orders.length > 0 && (
        <NewPagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNext={hasMore || currentPage < totalPages}
          onPageChange={setCurrentPage}
          itemsMessage={`${pageStartIndex}–${pageEndIndex} of ${totalItems}${hasMore ? '+' : ''} orders`}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 'var(--palette-light-2-value)', // 8px
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            minWidth: '240px',
            padding: 'var(--palette-light-2-value) 0 0', // 8px top
          },
          '& .MuiList-root': {
            padding: 0,
          },
        }}
      >
        {/* Change Status Section */}
        <Box sx={{ padding: 'var(--palette-light-4-value) var(--palette-light-6-value) var(--palette-light-2-value)', borderBottom: 'none' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Change Status
          </Typography>
        </Box>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.PENDING)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Pending
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.APPROVED)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Approved
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.CHANGING)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Changing
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.IN_PROGRESS)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          In Progress
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.DELIVERY)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Delivery
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange(OrderStatus.COMPLETED)}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Completed
        </MenuItem>

        {/* Refund or Cancel Section */}
        <Box sx={{ padding: 'var(--palette-light-4-value) var(--palette-light-6-value) var(--palette-light-2-value)', borderBottom: 'none', marginTop: 'var(--palette-light-2-value)' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
            Refund or cancel
          </Typography>
        </Box>
        <MenuItem 
          onClick={handleRefund}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Refund
        </MenuItem>
        <MenuItem 
          onClick={handleCancelOrder}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-status-danger-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            marginBottom: 'var(--palette-light-2-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Cancel order
        </MenuItem>
      </Menu>

      {/* Vendor Filter Menu */}
      <Menu
        anchorEl={vendorMenuAnchor}
        open={Boolean(vendorMenuAnchor)}
        onClose={() => setVendorMenuAnchor(null)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 'var(--palette-light-2-value)',
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            minWidth: '240px',
            maxHeight: '400px',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setSelectedVendorId(undefined);
            setVendorMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          All vendors
        </MenuItem>
        {vendorsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--palette-light-4-value)' }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          vendors.map((vendor) => (
            <MenuItem
              key={vendor.vendorId}
              onClick={() => {
                setSelectedVendorId(vendor.vendorId);
                setVendorMenuAnchor(null);
              }}
              sx={{
                fontSize: 'var(--palette-light-font-size-default-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
                '&:hover': {
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                },
              }}
            >
              {vendor.name}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Client Filter Menu */}
      <Menu
        anchorEl={clientMenuAnchor}
        open={Boolean(clientMenuAnchor)}
        onClose={() => setClientMenuAnchor(null)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 'var(--palette-light-2-value)',
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            minWidth: '240px',
            maxHeight: '400px',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setSelectedUserId(undefined);
            setClientMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          All clients
        </MenuItem>
        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--palette-light-4-value)' }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          users.map((user) => (
            <MenuItem
              key={user.userId}
              onClick={() => {
                setSelectedUserId(user.userId);
                setClientMenuAnchor(null);
              }}
              sx={{
                fontSize: 'var(--palette-light-font-size-default-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
                '&:hover': {
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                },
              }}
            >
              {user.name}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Status Filter Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 'var(--palette-light-2-value)',
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            minWidth: '240px',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setSelectedStatus(undefined);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          All statuses
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.PENDING);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.PENDING}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.APPROVED);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.APPROVED}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.CHANGING);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.CHANGING}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.IN_PROGRESS);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.IN_PROGRESS}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.DELIVERY);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.DELIVERY}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.COMPLETED);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.COMPLETED}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.CANCELLED);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.CANCELLED}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.DECLINED);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.DECLINED}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedStatus(OrderStatus.REFUNDED);
            setStatusMenuAnchor(null);
          }}
          sx={{
            fontSize: 'var(--palette-light-font-size-default-value)',
            color: 'var(--palette-light-text-primary-value-hex)',
            padding: 'var(--palette-light-3-value) var(--palette-light-6-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          {OrderStatus.REFUNDED}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Orders;